/**
 * Guests service
 * Provides typed functions for interacting with the guests table
 */

import { supabase } from '../supabase';
import type { Guest, GuestInsert } from '../types/database';

/**
 * Create a new guest registration
 */
export async function createGuest(guest: GuestInsert) {
  const { data, error } = await supabase
    .from('guests')
    .insert(guest)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to create guest: ${error.message}`);
  }

  return data as Guest;
}

/**
 * Fetch all guests registered by the current user
 * Only returns guests if user is Fellow or Admin
 */
export async function getMyGuests(
  userId: string | null | undefined
): Promise<Guest[]> {
  if (!userId) {
    throw new Error('User must be authenticated to fetch guests');
  }

  const { data, error } = await supabase
    .from('guests')
    .select('*')
    .eq('registered_by', userId)
    .order('visit_date', { ascending: false });

  if (error) {
    throw new Error(`Failed to fetch guests: ${error.message}`);
  }

  return data as Guest[];
}

/**
 * Fetch all guests (Admin only)
 * Used for export functionality
 */
export async function getAllGuests() {
  const { data, error } = await supabase
    .from('guests')
    .select('*')
    .order('visit_date', { ascending: false });

  if (error) {
    throw new Error(`Failed to fetch all guests: ${error.message}`);
  }

  return data as Guest[];
}

/**
 * Fetch guests (Admin only)
 * Supports pagination and date filtering
 */
export async function getGuestsWithProfiles(options?: {
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
}): Promise<Guest[]> {
  const { dateFrom, dateTo, limit = 20, offset = 0 } = options || {};

  let query = supabase
    .from('guests')
    .select('*')
    .order('visit_date', { ascending: false });

  if (dateFrom) {
    query = query.gte('visit_date', dateFrom);
  }

  if (dateTo) {
    query = query.lte('visit_date', dateTo);
  }

  query = query.range(offset, offset + limit - 1);

  const { data, error } = await query;

  if (error) {
    throw new Error(`Failed to fetch guests: ${error.message}`);
  }

  return (data as Guest[]) || [];
}

/**
 * Get count of guests registered in the last N days (Admin only)
 */
export async function getGuestCount(days: number): Promise<number> {
  const dateFrom = new Date();
  dateFrom.setDate(dateFrom.getDate() - days);
  dateFrom.setHours(0, 0, 0, 0); // Normalize to midnight local time

  const { count, error } = await supabase
    .from('guests')
    .select('*', { count: 'exact', head: true })
    .gte('visit_date', dateFrom.toISOString());

  if (error) {
    throw new Error(`Failed to get guest count: ${error.message}`);
  }

  return count || 0;
}

/**
 * Get count of guests visiting today (Admin only)
 * Filters by visit_date matching today's date (not last 24 hours)
 */
export async function getGuestCountToday(): Promise<number> {
  const now = new Date();
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);

  const startOfTomorrow = new Date(now);
  startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);
  startOfTomorrow.setHours(0, 0, 0, 0);

  const { count, error } = await supabase
    .from('guests')
    .select('*', { count: 'exact', head: true })
    .gte('visit_date', startOfToday.toISOString())
    .lt('visit_date', startOfTomorrow.toISOString());

  if (error) {
    throw new Error(`Failed to get today's guest count: ${error.message}`);
  }

  return count || 0;
}

/**
 * Get all guests matching date filter for export (Admin only)
 * Returns all matching guests, not paginated
 */
export async function getAllGuestsForExport(options?: {
  dateFrom?: string;
  dateTo?: string;
}): Promise<Guest[]> {
  const { dateFrom, dateTo } = options || {};

  let query = supabase
    .from('guests')
    .select('*')
    .order('visit_date', { ascending: false });

  if (dateFrom) {
    query = query.gte('visit_date', dateFrom);
  }

  if (dateTo) {
    query = query.lte('visit_date', dateTo);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(`Failed to fetch guests for export: ${error.message}`);
  }

  return (data as Guest[]) || [];
}
